import { getSystemIcon, SystemIcon } from '@helpers/icons';
import { Device, until } from '@helpers/media';
import { clamp } from '@helpers/utils';
import useMedia from '@hooks/useMedia';
import { useOutsideClick } from '@hooks/useOutsideClick';
import * as React from 'react';
import { isDropdownLink } from '../types';
import S from './NavigationLinks.styles';
const NavigationLinks = (props) => {
    const itemsRef = React.useRef([]);
    const containerRef = useOutsideClick(() => {
        props.setActive(-1);
    });
    const [activeColumn, setActiveColumn] = React.useState(-1);
    const isMobile = useMedia([until(Device.TabletLarge)], [true], false);
    if (!props.links || props.links.length === 0) {
        return null;
    }
    return (React.createElement(S.Container, { id: "main-nav", ref: containerRef, "aria-label": props.translations?.['global.navigation.label'] ?? '', onMouseLeave: () => {
            if (isMobile)
                return;
            props.setActive(-1);
        } },
        React.createElement(S.Links, { role: "menubar" }, props.links?.map((link, i) => (React.createElement("li", { key: link.id, role: "none" },
            React.createElement(S.TopLevelLink, { id: i === 0 ? 'nav-first-link' : undefined, role: "menuitem", ref: (el) => (itemsRef.current[i] = el), href: link.url, target: link.target, ...(isDropdownLink(link) && link.children && link.children.length > 0
                    ? {
                        'aria-haspopup': 'menu',
                        'aria-expanded': props.active === i,
                        onClick: (e) => {
                            if (isMobile) {
                                e.preventDefault();
                                e.stopPropagation();
                                props.setActive(props.active === i ? -1 : i);
                            }
                        },
                        onMouseEnter: () => {
                            if (isMobile) {
                                return;
                            }
                            props.setActive(i);
                        },
                        onFocus: () => {
                            if (isMobile) {
                                return;
                            }
                            props.setActive(i);
                        },
                    }
                    : {
                        onMouseEnter: () => {
                            if (isMobile) {
                                return;
                            }
                            props.setActive(-1);
                        },
                        onFocus: () => {
                            if (isMobile) {
                                return;
                            }
                            props.setActive(-1);
                        },
                    }), onKeyDown: (event) => {
                    const maxIndex = props.links.length - 1;
                    const nextIndex = i === maxIndex ? 0 : clamp(i + 1, maxIndex);
                    const prevIndex = i === 0 ? maxIndex : clamp(i - 1, maxIndex);
                    switch (event.key) {
                        case 'ArrowDown':
                        case 'Enter':
                            if (isDropdownLink(link) && link.children && link.children.length > 0) {
                                event.preventDefault();
                                event.stopPropagation();
                                props.setActive(i);
                            }
                            break;
                        case 'Escape':
                        case 'ArrowUp':
                            if (isDropdownLink(link) && link.children && link.children.length > 0) {
                                event.preventDefault();
                                event.stopPropagation();
                                props.setActive(-1);
                            }
                            break;
                        case 'ArrowRight':
                            event.preventDefault();
                            event.stopPropagation();
                            itemsRef.current[nextIndex]?.focus();
                            break;
                        case 'ArrowLeft':
                            event.preventDefault();
                            event.stopPropagation();
                            itemsRef.current[prevIndex]?.focus();
                            break;
                        case 'Home':
                            event.preventDefault();
                            event.stopPropagation();
                            itemsRef.current[0]?.focus();
                            break;
                        case 'End':
                            event.preventDefault();
                            event.stopPropagation();
                            itemsRef.current[maxIndex]?.focus();
                            break;
                        default:
                            break;
                    }
                } },
                link.text,
                isDropdownLink(link) && link.children && link.children.length > 0 ? (React.createElement(React.Fragment, null, getSystemIcon(SystemIcon.ChevronDown))) : null),
            isDropdownLink(link) && link.children && link.children.length > 0 ? (React.createElement(S.Dropdown, { role: "none", "data-type": link.type, "data-variant": link.variant },
                React.createElement("ul", { role: "menu", "aria-label": link.text },
                    React.createElement("li", { role: "none" },
                        React.createElement(S.BackButton, { onClick: () => props.setActive(-1), role: "menuitem" },
                            getSystemIcon(SystemIcon.ChevronDown),
                            ' ',
                            props.translations?.['global.navigation.back'])),
                    link.url ? (React.createElement("li", { role: "none" },
                        React.createElement(S.PrimaryPageLink, { href: link.url, target: link.target, role: "menuitem", tabIndex: props.active === i ? undefined : -1, onKeyDown: (event) => handleKeyPress(event, i) },
                            link.text,
                            " ",
                            React.createElement("span", null, getSystemIcon(SystemIcon.ArrowRight))))) : null,
                    link.variant === 'complex' ? (React.createElement(React.Fragment, null,
                        React.createElement("li", { role: "none" },
                            React.createElement(S.CardWrapper, { role: "menu" }, link.cards?.map((card, j) => (React.createElement("li", { role: "none", key: `${link.id}-card-${j}` },
                                React.createElement(S.Card, { href: card.url, target: card.target, role: "menuitem", tabIndex: props.active === i ? undefined : -1, onKeyDown: (event) => handleKeyPress(event, i) },
                                    React.createElement("span", null, getSystemIcon(card.icon ?? SystemIcon.None)),
                                    ' ',
                                    card.text)))))),
                        link.children.map((column, j) => (React.createElement("li", { role: "none", key: `${link.id}-column-${j}` },
                            React.createElement(S.ColumnLink, { role: "menuitem", as: "button", onClick: () => {
                                    setActiveColumn(j);
                                    const dropdown = itemsRef.current[i]?.nextElementSibling;
                                    // Ensure the dropdown is scrolled to the top when a child column is opened on mobile
                                    if (dropdown) {
                                        dropdown.scrollTop = 0;
                                    }
                                }, "aria-expanded": activeColumn === j && i === props.active, onKeyDown: (event) => handleKeyPress(event, i) },
                                column.text,
                                " ",
                                getSystemIcon(SystemIcon.ChevronDown)),
                            React.createElement(S.ColumnLinks, { role: "menu", "aria-labelledby": `${link.id}-${j}-label`, "data-active": activeColumn === j && i === props.active },
                                React.createElement("li", { role: "none" },
                                    React.createElement(S.BackButton, { role: "menuitem", onClick: () => setActiveColumn(-1) },
                                        getSystemIcon(SystemIcon.ChevronDown),
                                        ' ',
                                        props.translations?.['global.navigation.back'])),
                                React.createElement("li", { role: "none", id: `${link.id}-${j}-label` },
                                    React.createElement(S.ColumnLink, { "data-heading": true, ...(column.url
                                            ? {
                                                href: column.url,
                                                target: column.target,
                                                onKeyDown: (event) => handleKeyPress(event, i),
                                            }
                                            : { as: 'span' }), role: "menuitem", tabIndex: !isMobile
                                            ? props.active === i
                                                ? undefined
                                                : -1
                                            : activeColumn === j && props.active === i
                                                ? undefined
                                                : -1 }, column.text)),
                                column.children?.map((child, k) => (React.createElement("li", { role: "none", key: `${link.id}-child-${k}` },
                                    React.createElement(S.ColumnLink, { href: child.url, target: child.target, role: "menuitem", tabIndex: !isMobile
                                            ? props.active === i
                                                ? undefined
                                                : -1
                                            : activeColumn === j && props.active === i
                                                ? undefined
                                                : -1, onKeyDown: (event) => handleKeyPress(event, i) }, child.text)))))))))) : (React.createElement(React.Fragment, null, link.children.map((child, j) => (React.createElement("li", { role: "none", key: `${link.id}-child-${j}` },
                        React.createElement(S.ColumnLink, { href: child.url, target: child.target, role: "menuitem", tabIndex: props.active === i ? undefined : -1, onKeyDown: (event) => handleKeyPress(event, i) }, child.text))))))))) : null))))));
    function handleKeyPress(event, index) {
        switch (event.key) {
            case 'Escape':
            case 'ArrowUp':
                event.preventDefault();
                event.stopPropagation();
                props.setActive(-1);
                itemsRef.current[index]?.focus();
                break;
            default:
                break;
        }
    }
};
export default NavigationLinks;
